/*
 * cart-drawer.js — cart-drawer cluster
 *
 * Re-implements four custom elements that used to be supplied by the deleted
 * remote vendor bundle (the vendor runtime, formerly
 * loaded at layout/theme.liquid:28-36):
 *
 *   <cart-drawer>          class CartDrawer
 *   <cart-drawer-items>    class CartDrawerItems extends CartItems
 *   <cart-note>            class CartNote
 *   <cart-remove-button>   class CartRemoveButton
 *
 * Standard components are ports of Shopify's open-source Dawn theme (MIT):
 *   Dawn assets/cart.js         -> CartItems, CartNote, CartRemoveButton
 *   Dawn assets/cart-drawer.js  -> CartDrawer, CartDrawerItems
 * adapted to the DOM this theme actually renders. Every selector below is
 * annotated with the Liquid file + line it was read from.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE IS AN IIFE (deliberate, differs from assets/predictive-search.js)
 * ---------------------------------------------------------------------------
 * predictive-search.js must expose a bare top-level `SearchForm` because
 * assets/main-search.js:1 does `class MainSearch extends SearchForm`. Nothing
 * in this theme reads `CartItems`/`CartDrawer`/`CartNote`/`CartRemoveButton` as
 * a bare identifier (grepped every assets/*.js and *.liquid — the only external
 * consumer is assets/quick-add.js:9-10, which uses
 * `document.querySelector('cart-drawer')`, i.e. the DOM, not the class name).
 *
 * Meanwhile the `<cart-items>` tag belongs to a SEPARATE cluster that also
 * needs the CartItems base. If both files declared a top-level `class CartItems`
 * in classic scripts, the second one to parse would die with
 * "Identifier 'CartItems' has already been declared" and take its whole file
 * with it. Everything here therefore lives inside this IIFE and is published
 * only as a plain window property, which cannot collide with a later lexical
 * declaration. The `<cart-items>` cluster can either do
 *
 *     customElements.define('cart-items', class extends window.CartItems {});
 *
 * (this base already implements the cart-page section list) or ship its own
 * copy — both work.
 *
 * ---------------------------------------------------------------------------
 * RUNTIME CONTRACTS THIS FILE MUST HONOUR
 * ---------------------------------------------------------------------------
 * 1. Every cart write goes through the GLOBAL `window.fetch`. snippets/
 *    free-gifts.liquid:915-928 monkey-patches it (and :930-944 patches XHR),
 *    matching CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i
 *    (:494) to drive gift reconciliation and the progress bars. A captured
 *    native fetch reference would silently break that feature.
 * 2. No child node references are cached across renders. free-gifts
 *    (snippets/free-gifts.liquid:631-661) replaces `.drawer__inner` innerHTML,
 *    or the innerHTML of `#CartDrawer-CartItems` / `.drawer__footer` /
 *    `.drawer__header`, out from under these elements. `<cart-drawer>` itself
 *    is never replaced, so its constructor does not re-run — `#CartDrawer-Overlay`
 *    in particular is re-queried and re-bound after every render.
 * 3. `is-empty` on `<cart-drawer>` (snippets/cart-drawer.liquid:29) lives on the
 *    custom element, which is OUTSIDE every replace target, so it is synced by
 *    hand after each render. snippets/free-gifts.liquid:893-902 audits this flag
 *    against /cart.js and force-refetches the section when it disagrees.
 * 4. `#CartDrawer-Form` submit is NEVER intercepted. The checkout button is a
 *    real submit (snippets/cart-drawer.liquid:660-665) and the <noscript>
 *    update button at :655 relies on native submission.
 *
 * ES2019 only: no optional chaining, no nullish coalescing, no imports.
 */

(function () {
  'use strict';

  /* ======================================================================= *
   * Shared helpers.
   *
   * `trapFocus`, `removeTrapFocus`, `onKeyUpEscape`, `debounce`, `fetchConfig`
   * and `ON_CHANGE_DEBOUNCE_TIMER` are USED by shipped theme assets but DEFINED
   * nowhere in the theme — they came from the deleted vendor bundle. Another
   * cluster owns the shared-globals module, so this file must CONSUME those
   * globals and never claim the names. Each wrapper below prefers the global
   * when one exists and otherwise falls back to a private, identically-behaving
   * copy under a clearly non-colliding name.
   *
   * `typeof x` on an undeclared identifier is safe, but on a TDZ `let`/`const`
   * binding it throws — hence the try/catch around every probe.
   * ======================================================================= */

  /** Read a global by name without throwing on undeclared / TDZ identifiers. */
  function cdGlobal(name) {
    try {
      if (typeof window !== 'undefined' && typeof window[name] !== 'undefined') return window[name];
    } catch (e) {
      /* ignore */
    }
    return undefined;
  }

  /** Private stand-in for the missing global `debounce`. Dawn semantics. */
  function cdDebounceFallback(fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function cdDebounce(fn, wait) {
    var shared = cdGlobal('debounce');
    if (typeof shared === 'function') {
      try {
        return shared(fn, wait);
      } catch (e) {
        /* fall through to the private copy */
      }
    }
    return cdDebounceFallback(fn, wait);
  }

  /** Dawn's ON_CHANGE_DEBOUNCE_TIMER is 300ms. Prefer the shared global if set. */
  function cdChangeDebounceTimer() {
    var shared = cdGlobal('ON_CHANGE_DEBOUNCE_TIMER');
    return typeof shared === 'number' ? shared : 300;
  }

  /**
   * Dawn's `fetchConfig`. Cart writes must be JSON in / JavaScript out so the
   * Section Rendering payload comes back on `parsedState.sections`.
   */
  function cdFetchConfig(type) {
    var shared = cdGlobal('fetchConfig');
    if (typeof shared === 'function') {
      try {
        return shared(type || 'javascript');
      } catch (e) {
        /* fall through */
      }
    }
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + (type || 'javascript')
      }
    };
  }

  /**
   * `trapFocus(container, elementToFocus)` — signature confirmed from the
   * unguarded call sites in assets/secondary.js (MenuDrawer, ModalDialog).
   * It is a hard dependency there; here it is optional so a missing globals
   * module degrades to a plain `.focus()` instead of breaking the cart.
   */
  function cdTrapFocus(container, elementToFocus) {
    if (!container) return;
    var shared = cdGlobal('trapFocus');
    if (typeof shared === 'function') {
      try {
        shared(container, elementToFocus);
        return;
      } catch (e) {
        /* fall through */
      }
    }
    var target = elementToFocus || container;
    if (target && typeof target.focus === 'function') {
      try {
        target.focus();
      } catch (e) {
        /* ignore */
      }
    }
  }

  function cdRemoveTrapFocus(elementToFocus) {
    var shared = cdGlobal('removeTrapFocus');
    if (typeof shared === 'function') {
      try {
        shared(elementToFocus);
        return;
      } catch (e) {
        /* fall through */
      }
    }
    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      try {
        elementToFocus.focus();
      } catch (e) {
        /* ignore */
      }
    }
  }

  /** Dawn's `onKeyUpEscape` — closes the nearest open <details> on Escape. */
  function cdOnKeyUpEscape(event) {
    var shared = cdGlobal('onKeyUpEscape');
    if (typeof shared === 'function') {
      try {
        shared(event);
        return;
      } catch (e) {
        /* fall through */
      }
    }
    if (event.code && event.code.toUpperCase() !== 'ESCAPE') return;
    var openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;
    var summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    if (!summaryElement) return;
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
  }

  /**
   * Wraps an event handler so a throw inside it can never bubble out and abort
   * whatever triggered it (notably an in-flight add-to-cart).
   */
  function cdSafe(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (error) {
        if (window.console && console.error) console.error('[cart-drawer]', error);
      }
    };
  }

  /** `window.routes` is defined at layout/theme.liquid:671-680. */
  function cdRoutes() {
    return window.routes || {};
  }

  /** `window.cartStrings` is defined at layout/theme.liquid:681-684. */
  function cdCartStrings() {
    return window.cartStrings || { error: '', quantityError: '' };
  }

  /** First element found for an ordered list of ids. Never throws. */
  function cdFirstById(ids) {
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (el) return el;
    }
    return null;
  }

  /**
   * Line numbers arrive from `data-index` attributes. Only ever build a
   * selector from one after confirming it is a plain integer, so a malformed
   * attribute produces a miss rather than a SyntaxError from querySelector.
   */
  function cdSafeLine(line) {
    var str = String(line == null ? '' : line);
    return /^[0-9]+$/.test(str) ? str : null;
  }

  /**
   * Match assets/cart-notification.js:59-63 exactly in shape, plus a null guard
   * so a section that came back without the expected wrapper cannot throw.
   */
  function cdSectionInnerHTML(html, selector) {
    if (typeof html !== 'string') return null;
    var node = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector || '.shopify-section');
    return node ? node.innerHTML : null;
  }

  /* ======================================================================= *
   * CartItems — shared base.
   *
   * Owns the quantity-change -> POST /cart/change -> section-render pipeline
   * for BOTH render sites. This class is configured for the CART PAGE
   * (`<cart-items>`, sections/main-cart-items.liquid:15); CartDrawerItems below
   * overrides the id preferences and the section list for the drawer.
   *
   * NOTE: this file does NOT register `cart-items` — that tag belongs to a
   * different cluster. The class is published as `window.CartItems` for it.
   * ======================================================================= */

  var CartItems = (function () {
    function CartItems() {
      var self = Reflect.construct(HTMLElement, [], CartItems);
      return self;
    }
    return CartItems;
  })();

  // Written as a real class so `extends HTMLElement` works in every browser
  // that supports custom elements; the wrapper above is discarded.
  CartItems = class CartItems extends HTMLElement {
    constructor() {
      super();
      // Bound on the ELEMENT, not on the inputs: free-gifts replaces the
      // innerHTML of `#CartDrawer-CartItems` (snippets/free-gifts.liquid:652-658)
      // and Dawn's own render replaces `.js-contents` — inner listeners would
      // not survive either.
      this.addEventListener(
        'change',
        cdDebounce(
          cdSafe(
            function (event) {
              this.onChange(event);
            }.bind(this)
          ),
          cdChangeDebounceTimer()
        )
      );
    }

    /* ---- id resolution ---------------------------------------------------
     * On /cart BOTH render sites are live at once: `<cart-items>`
     * (sections/main-cart-items.liquid:15) and `<cart-drawer>`
     * (layout/theme.liquid:650-652, settings.cart_type == "drawer"). Dawn's
     * literal `document.getElementById(A) || document.getElementById(B)` would
     * therefore let the DRAWER instance drive the CART PAGE's status nodes and
     * quantity inputs. Each subclass instead declares its own preference order
     * and the shared lookups walk it — a deliberate fork from Dawn, see the
     * "assumptions" note in the handover.
     * -------------------------------------------------------------------- */

    /** Ordered candidate ids/prefixes for the cart page. */
    get domIds() {
      return {
        // sections/main-cart-items.liquid:352 / snippets/cart-drawer.liquid:474
        lineItemStatus: ['shopping-cart-line-item-status', 'CartDrawer-LineItemStatus'],
        // sections/main-cart-items.liquid:351 / snippets/cart-drawer.liquid:473
        liveRegion: ['cart-live-region-text', 'CartDrawer-LiveRegionText'],
        // sections/main-cart-items.liquid:44 / snippets/cart-drawer.liquid:136
        contents: ['main-cart-items', 'CartDrawer-CartItems'],
        // sections/main-cart-footer.liquid:75 / snippets/cart-drawer.liquid:478
        cartErrors: ['cart-errors', 'CartDrawer-CartErrors'],
        // sections/main-cart-items.liquid:248 / snippets/cart-drawer.liquid:372
        quantityPrefixes: ['Quantity-', 'Drawer-quantity-'],
        // sections/main-cart-items.liquid:272 / snippets/cart-drawer.liquid:391
        lineErrorPrefixes: ['Line-item-error-', 'CartDrawer-LineItemError-'],
        // sections/main-cart-items.liquid:72 / snippets/cart-drawer.liquid:142
        lineItemPrefixes: ['CartItem-', 'CartDrawer-Item-']
      };
    }

    get lineItemStatusElement() {
      return cdFirstById(this.domIds.lineItemStatus);
    }

    byPrefix(prefixes, line) {
      var safe = cdSafeLine(line);
      if (safe === null) return null;
      return cdFirstById(
        prefixes.map(function (prefix) {
          return prefix + safe;
        })
      );
    }

    /* ---- section rendering ---------------------------------------------- */

    /**
     * Cart-page section list. `data-id` is read from the DOM rather than
     * hard-coded because templates/cart.json names the sections `cart-items`
     * and `cart-footer` while the section FILES are main-cart-items /
     * main-cart-footer (sections/main-cart-items.liquid:44,
     * sections/main-cart-footer.liquid:1).
     */
    getSectionsToRender() {
      var sections = [];
      var mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        sections.push({
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-contents' // sections/main-cart-items.liquid:45
        });
      }
      // sections/header.liquid:767 (the <a id="cart-icon-bubble">) fed by
      // sections/cart-icon-bubble.liquid.
      sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
      // sections/cart-live-region-text.liquid; target node at
      // sections/main-cart-items.liquid:351.
      sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });

      var mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter) {
        sections.push({
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          selector: '.js-contents' // sections/main-cart-footer.liquid:23
        });
      }
      return sections;
    }

    getSectionInnerHTML(html, selector) {
      return cdSectionInnerHTML(html, selector);
    }

    /**
     * Replace every declared section. Resolution matches Dawn: look for
     * `selector` inside the target id, else fall back to the target itself
     * (that fallback is what makes `#cart-icon-bubble`, an <a> with no
     * `.shopify-section` child, work).
     */
    renderSections(parsedSections) {
      if (!parsedSections) return;
      this.getSectionsToRender().forEach(function (section) {
        var container = document.getElementById(section.id);
        if (!container) return;
        var elementToReplace = (section.selector && container.querySelector(section.selector)) || container;
        var markup = cdSectionInnerHTML(parsedSections[section.section], section.selector);
        if (markup !== null) elementToReplace.innerHTML = markup;
      });
    }

    /* ---- interaction ----------------------------------------------------- */

    onChange(event) {
      var target = event.target;
      if (!target || !target.dataset || typeof target.dataset.index === 'undefined') return;
      // `data-index` snippets/cart-drawer.liquid:373 (sections/main-cart-items.liquid:249),
      // `data-quantity-variant-id` snippets/cart-drawer.liquid:360,
      // and the focused control's `name` — "minus"/"plus" at
      // snippets/cart-drawer.liquid:345/:375, or "updates[]" at :361.
      var activeName = document.activeElement ? document.activeElement.getAttribute('name') : null;
      this.updateQuantity(target.dataset.index, target.value, activeName, target.dataset.quantityVariantId);
    }

    updateQuantity(line, quantity, name, variantId) {
      this.enableLoading(line);

      var self = this;
      var sectionsToRender = this.getSectionsToRender();
      var body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: sectionsToRender.map(function (section) {
          return section.section;
        }),
        sections_url: window.location.pathname
      });

      // GLOBAL window.fetch on purpose — see contract note 1 at the top.
      window
        .fetch(cdRoutes().cart_change_url, Object.assign({}, cdFetchConfig('javascript'), { body: body }))
        .then(function (response) {
          return response.text();
        })
        .then(function (state) {
          var parsedState = JSON.parse(state);
          var quantityElement = self.byPrefix(self.domIds.quantityPrefixes, line);
          // Counted BEFORE the render so the comparison below is against the
          // pre-update line count.
          var items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            // Restore the server-rendered value; `value` attribute at
            // snippets/cart-drawer.liquid:361 / sections/main-cart-items.liquid:243.
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            self.updateLiveRegions(line, parsedState.errors);
            return;
          }

          var isEmpty = parsedState.item_count === 0;
          self.classList.toggle('is-empty', isEmpty);

          var cartFooter = document.getElementById('main-cart-footer'); // sections/main-cart-footer.liquid:1
          if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

          // MANDATORY: `is-empty` sits on <cart-drawer> itself
          // (snippets/cart-drawer.liquid:29), outside every replace target.
          // snippets/free-gifts.liquid:893-902 treats a mismatch as a bug and
          // force-refetches ?section_id=cart-drawer.
          var cartDrawerWrapper = document.querySelector('cart-drawer');
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);

          self.renderSections(parsedState.sections);

          // NOTE: for the drawer subclass `self` is DETACHED from here on —
          // the render above replaced `.drawer__inner`, which contained it.
          // Everything below therefore goes through `document`.

          var updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          var message = '';
          if (items.length === parsedState.items.length && quantityElement && updatedValue !== parseInt(quantityElement.value, 10)) {
            if (typeof updatedValue === 'undefined') {
              message = cdCartStrings().error;
            } else {
              message = cdCartStrings().quantityError.replace('[quantity]', updatedValue);
            }
          }
          self.updateLiveRegions(line, message);

          self.restoreFocus(line, name, isEmpty, cartDrawerWrapper);
        })
        .catch(
          cdSafe(function (error) {
            if (window.console && console.error) console.error('[cart-drawer] cart change failed', error);
            document.querySelectorAll('.loading-overlay').forEach(function (overlay) {
              overlay.classList.add('hidden'); // snippets/cart-drawer.liquid:147
            });
            var errors = cdFirstById(self.domIds.cartErrors);
            if (errors) errors.textContent = cdCartStrings().error;
          })
        )
        .then(function () {
          // Stand-in for `.finally()` so this stays ES2019-safe.
          self.disableLoading(line);
        });
    }

    /**
     * Dawn's post-render focus restore, re-resolved through `document` because
     * the nodes are brand new after the section swap.
     */
    restoreFocus(line, name, isEmpty, cartDrawerWrapper) {
      var lineItem = this.byPrefix(this.domIds.lineItemPrefixes, line);
      var control = null;
      if (lineItem && name) {
        try {
          control = lineItem.querySelector('[name="' + name + '"]');
        } catch (e) {
          control = null;
        }
      }

      if (control) {
        // Buttons are `name="minus"` / `name="plus"`
        // (snippets/cart-drawer.liquid:345, :375); the input is `name="updates[]"` (:361).
        if (cartDrawerWrapper) {
          cdTrapFocus(cartDrawerWrapper, control);
        } else if (typeof control.focus === 'function') {
          control.focus();
        }
        return;
      }

      if (isEmpty && cartDrawerWrapper) {
        // `.drawer__inner-empty` snippets/cart-drawer.liquid:40; its first link
        // is the continue-shopping button at :52-56.
        var emptyContainer = cartDrawerWrapper.querySelector('.drawer__inner-empty');
        if (emptyContainer) cdTrapFocus(emptyContainer, emptyContainer.querySelector('a'));
        return;
      }

      if (document.querySelector('.cart-item') && cartDrawerWrapper) {
        // `.cart-item__name` snippets/cart-drawer.liquid:208-210,
        // sections/main-cart-items.liquid:97.
        cdTrapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
      }
    }

    updateLiveRegions(line, message) {
      var lineItemError = this.byPrefix(this.domIds.lineErrorPrefixes, line);
      if (lineItemError) {
        var errorText = lineItemError.querySelector('.cart-item__error-text');
        // base.css `.cart-item__error-text:empty + svg{display:none}` — writing
        // '' is what hides the error icon again.
        if (errorText) errorText.innerHTML = message;
      }

      var statusElement = this.lineItemStatusElement;
      if (statusElement) statusElement.setAttribute('aria-hidden', true);

      var cartStatus = cdFirstById(this.domIds.liveRegion);
      if (!cartStatus) return;
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(function () {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    /* ---- loading state ---------------------------------------------------
     * Both directions resolve through `document`, never `this`: by the time
     * disableLoading runs, the drawer instance has usually been replaced along
     * with `.drawer__inner`, and a `this.querySelectorAll` would silently
     * no-op, leaving the fresh spinners visible forever.
     * -------------------------------------------------------------------- */

    lineOverlays(line) {
      var safe = cdSafeLine(line);
      if (safe === null) return [];
      // snippets/cart-drawer.liquid:147 and sections/main-cart-items.liquid:167,:289
      return Array.prototype.slice.call(
        document.querySelectorAll('#CartItem-' + safe + ' .loading-overlay, #CartDrawer-Item-' + safe + ' .loading-overlay')
      );
    }

    enableLoading(line) {
      var contents = cdFirstById(this.domIds.contents);
      // base.css `.cart__items--disabled{pointer-events:none}`. Only styles
      // anything on the cart page — `#CartDrawer-CartItems` carries
      // `drawer__contents js-contents` (snippets/cart-drawer.liquid:136), not
      // `cart__items` — but it is kept for Dawn parity.
      if (contents) contents.classList.add('cart__items--disabled');

      this.lineOverlays(line).forEach(function (overlay) {
        overlay.classList.remove('hidden');
      });

      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }

      var statusElement = this.lineItemStatusElement;
      if (statusElement) statusElement.setAttribute('aria-hidden', false);
    }

    disableLoading(line) {
      this.lineOverlays(line).forEach(function (overlay) {
        overlay.classList.add('hidden');
      });
      var contents = cdFirstById(this.domIds.contents);
      if (contents) contents.classList.remove('cart__items--disabled');
    }
  };

  /* ======================================================================= *
   * CartDrawerItems — snippets/cart-drawer.liquid:121-480.
   *
   * Same pipeline, drawer-scoped ids and a drawer-scoped section list.
   * ======================================================================= */

  class CartDrawerItems extends CartItems {
    /** Drawer-first preference order — the mirror image of the base's. */
    get domIds() {
      return {
        lineItemStatus: ['CartDrawer-LineItemStatus', 'shopping-cart-line-item-status'],
        liveRegion: ['CartDrawer-LiveRegionText', 'cart-live-region-text'],
        contents: ['CartDrawer-CartItems', 'main-cart-items'],
        cartErrors: ['CartDrawer-CartErrors', 'cart-errors'],
        quantityPrefixes: ['Drawer-quantity-', 'Quantity-'],
        lineErrorPrefixes: ['CartDrawer-LineItemError-', 'Line-item-error-'],
        lineItemPrefixes: ['CartDrawer-Item-', 'CartItem-']
      };
    }

    getSectionsToRender() {
      return [
        {
          // `#CartDrawer` snippets/cart-drawer.liquid:30,
          // `.drawer__inner` :33. Replacing the inner (not `#CartDrawer`) keeps
          // `#CartDrawer-Overlay` (:31) alive, so the overlay listener survives.
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner'
        },
        {
          // sections/header.liquid:767, fed by sections/cart-icon-bubble.liquid.
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }
  }

  /* ======================================================================= *
   * CartDrawer — snippets/cart-drawer.liquid:29-852.
   * ======================================================================= */

  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      // Bound on the element itself so it survives every inner innerHTML swap.
      this.addEventListener(
        'keyup',
        cdSafe(
          function (event) {
            if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
          }.bind(this)
        )
      );

      this.onOverlayClick = cdSafe(
        function () {
          this.close();
        }.bind(this)
      );

      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    }

    /**
     * `#CartDrawer-Overlay` (snippets/cart-drawer.liquid:31) lives INSIDE
     * `#CartDrawer` (:30), so it is destroyed by every render. Re-query and
     * re-bind rather than caching the node. The `_cartDrawerBound` flag keeps
     * repeat calls from stacking listeners on a node that happened to survive.
     */
    bindOverlay() {
      var overlay = this.querySelector('#CartDrawer-Overlay');
      if (!overlay || overlay._cartDrawerBound) return;
      overlay._cartDrawerBound = true;
      overlay.addEventListener('click', this.onOverlayClick);
    }

    /**
     * `#cart-icon-bubble` (sections/header.liquid:767) is a plain
     * `<a href="{{ routes.cart_url }}">` with no onclick, so this is the only
     * thing that turns it into the drawer opener. The section render replaces
     * the anchor's INNER html only, so the listeners installed here survive.
     */
    setHeaderCartIconAccessibility() {
      var cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink._cartDrawerBound) return;
      cartLink._cartDrawerBound = true;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      var self = this;
      cartLink.addEventListener(
        'click',
        cdSafe(function (event) {
          event.preventDefault();
          self.open(cartLink);
        })
      );
      cartLink.addEventListener(
        'keydown',
        cdSafe(function (event) {
          if (!event.code || event.code.toUpperCase() !== 'SPACE') return;
          event.preventDefault();
          self.open(cartLink);
        })
      );
    }

    /**
     * Public. Called from the header icon, from `renderContents`, and by any
     * other cluster holding a `<cart-drawer>` reference.
     */
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      // `#Details-CartDrawer > summary` snippets/cart-drawer.liquid:562-563.
      // Matches Dawn's `[id^="Details-"] summary` probe. Only wired once.
      var summary = this.querySelector('[id^="Details-"] summary');
      if (summary && !summary.hasAttribute('role')) this.setSummaryAccessibility(summary);

      var self = this;
      // Deferred a tick so the class change is a real transition start rather
      // than being coalesced with a same-frame render.
      setTimeout(function () {
        // `active` drives base.css `.drawer.active{visibility:visible;...}` and
        // `.drawer.active .drawer__inner{transform:translateX(0)}`. It is also
        // the class snippets/cart-drawer.liquid:29 emits for
        // `section.settings.test_mode`, which is what proves it is the
        // open-state class. `animate` is Dawn parity only — base.css has NO
        // `.drawer.animate` / `cart-drawer.animate` rule (verified: 0 matches).
        self.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        cdSafe(function () {
          var containerToTrapFocusOn = self.classList.contains('is-empty')
            ? self.querySelector('.drawer__inner-empty') // :40
            : document.getElementById('CartDrawer'); // :30
          var focusElement = self.querySelector('.drawer__inner') || self.querySelector('.drawer__close'); // :33 / :45,:87
          cdTrapFocus(containerToTrapFocusOn, focusElement);
        }),
        { once: true }
      );

      // base.css `.overflow-hidden{overflow:hidden}` + `body.overflow-hidden{height:100vh}`
      document.body.classList.add('overflow-hidden');
    }

    /**
     * Public, zero-arg callable — snippets/cart-drawer.liquid:47 and :89 render
     * `onclick="this.closest('cart-drawer').close()"` inline. Must never throw.
     */
    close() {
      this.classList.remove('active');
      cdRemoveTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    /**
     * Dawn's summary a11y wiring. `aria-controls` is skipped here because the
     * summary's nextElementSibling is `<cart-note>` (snippets/cart-drawer.liquid:569),
     * which has no id.
     */
    setSummaryAccessibility(summary) {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', 'false');

      var sibling = summary.nextElementSibling;
      if (sibling && sibling.getAttribute('id')) summary.setAttribute('aria-controls', sibling.id);

      summary.addEventListener(
        'click',
        cdSafe(function (event) {
          var details = event.currentTarget.closest('details');
          event.currentTarget.setAttribute('aria-expanded', !(details && details.hasAttribute('open')));
        })
      );

      if (summary.parentElement) summary.parentElement.addEventListener('keyup', cdSafe(cdOnKeyUpEscape));
    }

    /** Required by assets/quick-add.js:9-10. */
    setActiveElement(element) {
      this.activeElement = element;
    }

    /**
     * `#CartDrawer` snippets/cart-drawer.liquid:30 and `#cart-icon-bubble`
     * sections/header.liquid:767. `id` doubles as the key into
     * `parsedState.sections`; the bubble takes the default `.shopify-section`
     * selector, matching assets/cart-notification.js:59.
     */
    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      return cdSectionInnerHTML(html, selector);
    }

    /**
     * Public. 2-arg signature is the theme's local convention, established by
     * assets/cart-notification.js:33, so one product-form call site serves both
     * `settings.cart_type` values.
     */
    renderContents(parsedState, dontOpen) {
      if (!parsedState) return;
      var sections = parsedState.sections;
      if (!sections) return;

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach(function (section) {
        var sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;
        var markup = cdSectionInnerHTML(sections[section.id], section.selector);
        if (markup !== null) sectionElement.innerHTML = markup;
      });

      // THEME-SPECIFIC, NOT IN DAWN. `is-empty` is on <cart-drawer> itself
      // (snippets/cart-drawer.liquid:29), i.e. outside the `#CartDrawer`
      // replace target. Toggled AFTER the swap so the styling lands on the new
      // markup — the same order snippets/free-gifts.liquid:641-646 uses.
      // Prefer the freshly rendered markup, fall back to item_count.
      var freshEmpty = null;
      if (typeof sections['cart-drawer'] === 'string') {
        var freshDrawer = new DOMParser()
          .parseFromString(sections['cart-drawer'], 'text/html')
          .querySelector('cart-drawer');
        if (freshDrawer) freshEmpty = freshDrawer.classList.contains('is-empty');
      }
      if (freshEmpty === null && typeof parsedState.item_count === 'number') {
        freshEmpty = parsedState.item_count === 0;
      }
      if (freshEmpty !== null) this.classList.toggle('is-empty', freshEmpty);

      var self = this;
      setTimeout(function () {
        // The overlay node was just destroyed with `#CartDrawer`'s innerHTML.
        self.bindOverlay();
        if (!dontOpen) self.open();
      });
    }
  }

  /* ======================================================================= *
   * CartNote — snippets/cart-drawer.liquid:569-577 (drawer),
   *            sections/main-cart-footer.liquid:5-14 (cart page).
   *
   * Fire-and-forget note write. No response handling and NO section render on
   * purpose: snippets/free-gifts.liquid:915-928 fires refreshState() +
   * scheduleSync(150) on every /cart/update write, so re-rendering here would
   * make a keystroke burst thrash the drawer.
   *
   * In the drawer the textarea has no `form=` and sits in `.drawer__footer`,
   * OUTSIDE `#CartDrawer-Form` (snippets/cart-drawer.liquid:130-479) — this
   * fetch is the only way the note is ever saved from the drawer. On the cart
   * page the textarea carries `form="cart"`
   * (sections/main-cart-footer.liquid:10) and also posts natively, so nothing
   * here may preventDefault or otherwise interfere.
   * ======================================================================= */

  class CartNote extends HTMLElement {
    constructor() {
      super();

      // Bound on the element, not the textarea: the drawer footer's innerHTML
      // is replaced by both the section render and
      // snippets/free-gifts.liquid:653-657, which recreates <cart-note>
      // (auto-upgraded, so the constructor re-runs) rather than mutating it.
      this.addEventListener(
        'input',
        cdDebounce(
          cdSafe(function (event) {
            var target = event.target;
            if (!target || typeof target.value !== 'string') return;
            var body = JSON.stringify({ note: target.value });
            // GLOBAL window.fetch — see contract note 1.
            window
              .fetch(cdRoutes().cart_update_url, Object.assign({}, cdFetchConfig('javascript'), { body: body }))
              .catch(function (error) {
                if (window.console && console.error) console.error('[cart-drawer] note update failed', error);
              });
          }),
          cdChangeDebounceTimer()
        )
      );
    }
  }

  /* ======================================================================= *
   * CartRemoveButton — snippets/cart-drawer.liquid:312-325 (drawer),
   *                    sections/main-cart-items.liquid:259-270 (cart page).
   *
   * One class, two child shapes: a <button type="button"> in the drawer
   * (:318-324) and an <a href="{{ item.url_to_remove }}"> on the cart page
   * (:263-269). The listener therefore lives on the custom element, and
   * preventDefault is mandatory or the cart page does a full-page GET removal.
   *
   * Deliberately reads ONLY `data-index`. `data-handle`
   * (snippets/cart-drawer.liquid:316) has no consumer anywhere in the theme —
   * it is left to the upsell/gift cluster to claim.
   * ======================================================================= */

  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();

      var self = this;
      this.addEventListener(
        'click',
        cdSafe(function (event) {
          event.preventDefault();
          // `<cart-items>` sections/main-cart-items.liquid:15,
          // `<cart-drawer-items>` snippets/cart-drawer.liquid:121.
          var cartItems = self.closest('cart-items') || self.closest('cart-drawer-items');
          if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
          cartItems.updateQuantity(self.dataset.index, 0);
        })
      );
    }
  }

  /* ======================================================================= *
   * Registration.
   *
   * `cart-items` is intentionally NOT registered here — it belongs to a
   * different cluster. Every define is guarded so a double-load is a no-op.
   * ======================================================================= */

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', CartDrawerItems);
  }

  if (!customElements.get('cart-note')) {
    customElements.define('cart-note', CartNote);
  }

  if (!customElements.get('cart-remove-button')) {
    customElements.define('cart-remove-button', CartRemoveButton);
  }

  /* ======================================================================= *
   * Exports.
   *
   * Plain window properties, never top-level lexical declarations — see the
   * header comment. `window.CartItems` / `window.CartItemsBase` are the reuse
   * hook for the `<cart-items>` cluster.
   * ======================================================================= */

  window.CartItems = window.CartItems || CartItems;
  window.CartItemsBase = window.CartItemsBase || CartItems;
  window.CartDrawerItems = window.CartDrawerItems || CartDrawerItems;
  window.CartDrawer = window.CartDrawer || CartDrawer;
  window.CartNote = window.CartNote || CartNote;
  window.CartRemoveButton = window.CartRemoveButton || CartRemoveButton;
})();
